import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import lodash from "lodash";
import io from "socket.io-client";
import { Box, CircularProgress, Typography } from "@mui/material";
import momentTimeZone from "moment-timezone";

import getEventDetails from "../../api/getEventDetail";
import getEventItemsListAPI from "../../api/getEventItemsListAPI";
import getBidStatusAPI from "../../api/getBidStatusAPI";
import getMyLotsBuyerAPI from "../../api/getMyLotsBuyerAPI";
import AuctionDetails from "../../packages/auction-details";
import AuctionLotsContainer from "../../packages/auction-lots-container";
import useStyles from "../components/Auction/auction.style";
import { getToken } from "../../common";
import config from "../../config/config";
import { motion } from "framer-motion";
import TermsAndCatalogDialog from "./TermsAndCatalogDialog";
import acceptEventTerms from "../../api/acceptTermsForEventAPI";
import SidebarComponent from "./Sidebar";

const AuctionDetailPage = () => {
  const location = useParams();
  const auctionId = location.auctionId || location.source;
  const classes = useStyles();
  const timeZone =
    localStorage.getItem("currentTimeZone") || momentTimeZone.tz.guess();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [socket, setSocket] = useState(null);
  const [settingUpSocket, setSettingUpSocket] = useState(false);
  const [fetchingInProgress, setFetchingInProgress] = useState(false);
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const SOCKET_URL = config.api.socket_url;

  const [details, updateDetails] = useState({});
  const [httpStatus, updateHttpStatus] = useState({
    auctionLots: false,
    auctionBuyerLots: false,
  });
  const [auctionAllLots, updateAuctionAllLots] = useState([]);
  const [auctionUserLots, updateAuctionUserLots] = useState([]);
  const [showTermsAndCatalog, setShowTermsAndCatalog] = useState(false);

  const userData = (() => {
    if (!localStorage.getItem("userData")) {
      return {};
    }
    return JSON.parse(localStorage.getItem("userData"));
  })();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    setSocketListener();
    return () => {
      if (socket) {
        socket.disconnect();
        console.log("Socket Disconnected");
      }
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    if (userData) {
      socket.on(`${userData.userId}`, (props) => {
        socketListener(props);
      });
    } else if (details?.eventId) {
      socket.on(`${details?.eventId}`, (props) => {
        socketListener(props);
      });
    }

    // specific listener for lot updates (admin/seller changes)
    if (details?.eventId) {
      socket.emit("join-event", details.eventId); // Join the event room
      socket.on("LOT_UPDATED", (props) => {
        handleLotUpdate(props);
      });
    }

    return () => {
      if (userData) {
        socket.off(`${userData.userId}`);
      } else if (details?.eventId) {
        socket.off(`${details?.eventId}`);
      }
      if (details?.eventId) {
        socket.off("LOT_UPDATED");
      }
    };
  }, [userData, socket, details?.eventId]);

  const handleLotUpdate = (props) => {
    const updateLogic = (prevLots) => {
      return lodash.map(lodash.cloneDeep(prevLots), (details) => {
        if (details.eventItemId !== props.eventItemId) {
          return details;
        }
        // Merge updated fields
        return { ...details, ...props };
      });
    };

    updateAuctionAllLots((prevLots) => updateLogic(prevLots));
    updateAuctionUserLots((prevUserLots) => updateLogic(prevUserLots));
  };

  const loadInitialData = async () => {
    setLoadingInitialData(true);
    await httpRequest();
    setLoadingInitialData(false);
  };

  const setSocketListener = () => {
    if (!socket && !settingUpSocket) {
      setSettingUpSocket(true);
      const newSocket = io(SOCKET_URL, {
        extraHeaders: { authorization: getToken() },
      });
      setSocket(newSocket);
      setSettingUpSocket(false);
      console.log("Socket Connected");
    }
  };

  const updateListWithBidStatusAllotedLots = (auctionAllotedLots, listData) => {
    return lodash.map(listData, (details) => {
      const searchAuctionLot = lodash.find(
        auctionAllotedLots,
        ({ eventItemId }) => eventItemId === details.eventItemId
      );
      if (lodash.isUndefined(searchAuctionLot)) {
        details.myBidStatus = {
          myLastBid: 0,
          myBidRank: "0",
          status: "error",
          authorized: false,
        };
      } else {
        details.myBidStatus = searchAuctionLot.myBidStatus;
      }

      return details;
    });
  };

  const updateListWithDefaultBidStatus = (listData) =>
    listData.map((details) => ({
      ...details,
      myBidStatus: {
        myLastBid: 0,
        myBidRank: "0",
        status: "error",
        authorized: false,
      },
    }));

  const updateListWithBidStatus = async (listData) =>
    Promise.all(
      listData.map(async (object) => {
        try {
          const { data } = await getBidStatusAPI(
            object.eventId,
            object.eventItemId,
            { timeZone }
          );
          return { ...object, myBidStatus: data };
        } catch (error) {
          return {
            ...object,
            myBidStatus: {
              myLastBid: 0,
              myBidRank: "0",
              status: "error",
              authorized: true,
            },
          };
        }
      })
    );

  const httpRequest = async () => {
    try {
      if (fetchingInProgress) return;
      setFetchingInProgress(true);
      const data = { auctionLots: [], auctionBuyerLots: [] };
      const detailsResponse = await getEventDetails(
        { auctionId: auctionId },
        { timeZone }
      );

      if (detailsResponse.status === 200) {
        updateDetails({ ...detailsResponse.data });
        // check if buyer have accepted the terms and conditions
        if (detailsResponse.data.acceptedTerms === false) {
          // terms and conditions not accepted
          setShowTermsAndCatalog(true);
          return;
        }
      }
      const listResponse = await getEventItemsListAPI(
        { itemId: auctionId },
        { timeZone }
      );

      if (listResponse.status === 200) {
        if (lodash.isEmpty(userData)) {
          data.auctionLots = updateListWithDefaultBidStatus(
            listResponse.data.data
          );
        } else {
          const { status, data: response } = await getMyLotsBuyerAPI(
            {
              eventId: auctionId,
            },
            { timeZone }
          );

          if (status !== 200) {
            data.auctionLots = updateListWithDefaultBidStatus(
              listResponse.data.data
            );
          } else {
            data.auctionBuyerLots = await updateListWithBidStatus(
              response.data ?? []
            );
            data.auctionLots = updateListWithBidStatusAllotedLots(
              data.auctionBuyerLots,
              listResponse.data.data
            );
          }
        }
        updateAuctionAllLots([...data.auctionLots]);
        updateAuctionUserLots([...data.auctionBuyerLots]);
        updateHttpStatus({ auctionLots: true, auctionBuyerLots: true });
        if (!socket) {
          setSocketListener();
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setFetchingInProgress(false);
    }

    return true;
  };

  const handleEnglishAuctionSocket = (props) => {
    updateAuctionAllLots((prevLots) => {
      return lodash.map(lodash.cloneDeep(prevLots), (details) => {
        if (details.eventItemId !== props.eventItemId) {
          return details;
        }

        details.myBidStatus = props;
        details.highestBid = props.highestBid;
        details.nextBidAmount = props.nextBidAmount;
        details.endDateAndTime = props.updatedEndTime;

        return { ...details };
      });
    });

    updateAuctionUserLots((prevUserLots) => {
      return lodash.map(lodash.cloneDeep(prevUserLots), (details) => {
        if (details.eventItemId !== props.eventItemId) {
          return details;
        }

        details.myBidStatus = props;
        details.highestBid = props.highestBid;
        details.nextBidAmount = props.nextBidAmount;
        details.endDateAndTime = props.updatedEndTime;

        return { ...details };
      });
    });
  };

  const handleDutchAuctionSocket = (props) => {
    const receivedLots = props?.data || [];
    const structuredReceivedLots = {};

    receivedLots.forEach((lot) => {
      structuredReceivedLots[lot.eventItemId] = {
        ...lot,
      };
    });

    updateAuctionAllLots((prevLots) => {
      return prevLots
        .map((details) => {
          return {
            ...details,
            ...(structuredReceivedLots[details.eventItemId] || {}),
          };
        })
        .filter((lot) => lot.concluded !== "YES");
    });

    updateAuctionUserLots((prevUserLots) => {
      return prevUserLots
        .map((details) => {
          return {
            ...details,
            ...(structuredReceivedLots[details.eventItemId] || {}),
          };
        })
        .filter((lot) => lot.concluded !== "YES");
    });
  };

  const handleJapaneseAuctionSocket = (props) => {
    const receivedLots = props?.data || [];
    const structuredReceivedLots = {};

    receivedLots.forEach((lot) => {
      structuredReceivedLots[lot.eventItemId] = {
        ...lot,
      };
    });

    updateAuctionAllLots((prevLots) => {
      return prevLots.map((details) => {
        return {
          ...details,
          ...(structuredReceivedLots[details.eventItemId] || {}),
        };
      });
    });

    updateAuctionUserLots((prevUserLots) => {
      return prevUserLots.map((details) => {
        return {
          ...details,
          ...(structuredReceivedLots[details.eventItemId] || {}),
        };
      });
    });
  };

  const socketListener = (props) => {
    try {
      if (!props) return;

      switch (details?.eventFormat) {
        case "ENGLISH_AUCTION":
          handleEnglishAuctionSocket(props);
          break;
        case "DUTCH_AUCTION":
          handleDutchAuctionSocket(props);
          break;
        case "JAPANESE_AUCTION":
          handleJapaneseAuctionSocket(props);
          break;
        default:
          break;
      }
    } catch (error) {
      console.log(error);
    }
  };

  // filter lot list and remove the lot which has been ended
  const removeEndedLot = (id) => {
    const updatedAllLots = auctionAllLots?.filter(
      (lot) => lot.eventItemId != id
    );

    const updatedUserLots = auctionUserLots?.filter(
      (lot) => lot.eventItemId != id
    );

    updateAuctionAllLots(updatedAllLots);
    updateAuctionUserLots(updatedUserLots);
  };

  // update lot list & add finalize flag
  const finalizeLot = (id) => {
    const updatedAllLots = auctionAllLots?.map((lot) => {
      if (lot.eventItemId != id) return lot;
      else {
        return {
          ...lot,
          finalizing: true,
        };
      }
    });

    const updatedUserLots = auctionUserLots?.map((lot) => {
      if (lot.eventItemId != id) return lot;
      else {
        return {
          ...lot,
          finalizing: true,
        };
      }
    });

    updateAuctionAllLots(updatedAllLots);
    updateAuctionUserLots(updatedUserLots);

    setTimeout(() => {
      // remove lot
      removeEndedLot(id);
    }, 5000);
  };

  const handleTermsAndCatalogDialogClose = async (accepted, ipAddress) => {
    if (!accepted) {
      return;
    }

    // if accepted, update the details and set showTermsAndCatalog to false
    try {
      // accept terms and conditions
      // 1. make API call to accept terms and conditions
      const _acceptTermsResponse = await acceptEventTerms({
        auctionId,
        ipAddress,
      });

      // 2. update the details state with the new data
      // 3. set showTermsAndCatalog to false
      setShowTermsAndCatalog(false);
      // 4. load only lots data
      loadInitialData();
    } catch (error) {
      console.log(error);
    }
  };

  if (loadingInitialData) {
    return (
      <Box className={classes.auction}>
        <Box className={classes.auctionDetail}>
          <Box className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "30vh",
                width: "100%",
                textAlign: "center",
              }}
            >
              <CircularProgress
                sx={{
                  color: "#2D55EB",
                  width: "5rem",
                  height: "5rem",
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "#444",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                }}
              >
                Just a moment... fetching auction details
              </Typography>
              <Typography variant="body2" sx={{ color: "#888", mt: 1 }}>
                We’re gathering the latest data for you.
              </Typography>
            </motion.div>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <div className={classes.auction}>
      <SidebarComponent mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Box className={classes.auctionDetail}>
        <Box className="container">
          <AuctionDetails eventsDetails={details}></AuctionDetails>
          <TermsAndCatalogDialog
            show={details.acceptedTerms === false}
            handleClose={handleTermsAndCatalogDialogClose}
            catalog={details?.auctionCatalogueUrl}
          />
          <AuctionLotsContainer
            allLots={auctionAllLots}
            userLots={auctionUserLots}
            eventsDetails={details}
            refetchLotDetails={httpRequest}
            finalizeLot={finalizeLot}
            auctionId={auctionId}
          />
        </Box>
      </Box>
    </div>
  );
};

export default AuctionDetailPage;
